import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  Clock,
  MapPin,
  Plus,
  ChevronLeft,
  Ticket,
  Check,
  Map as MapIcon,
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import Avatar from './Avatar';
import EventCardImage from './EventCardImage';
import {
  EVENT_IMAGE_LAYOUT,
  EVENT_IMAGE_SLOT,
  getEventImageUrl,
} from '../lib/images/eventImageUrl';
import RsvpGoingOptionsModal from './rsvp/GoingOptionsModal';
import FestivalMatchSetupModal from './lineup/FestivalMatchSetupModal';
import ArtistLinksRow from './lineup/ArtistLinksRow';
import EventLineupSection from './lineup/EventLineupSection';
import FestivalGroupCard from './lineup/FestivalGroupCard';
import { useJoinableEventGroups } from '../hooks/useJoinableEventGroups';
import { useEventHeadliners } from '../hooks/queries/useEventHeadliners';
import MutualVotersPanel from './lineup/MutualVotersPanel';
import RejoinModal from './rsvp/RejoinModal';
import UnRsvpConfirm from './rsvp/UnRsvpConfirm';
import EditRsvpSheet from './rsvp/EditRsvpSheet';
import EventRsvpBar from './rsvp/EventRsvpBar';
import { useToast } from '../contexts/ToastContext';
import { appLog } from '../lib/appLog';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';

import { isEventActionClosed } from '../utils/eventCutoff';

import { classifyMatchUserToGroupPayload } from '../utils/matchJoinOutcomes';
import { shareLink, copyTextToClipboard } from '../lib/shareLink';
import { buildAppUrl } from '../lib/siteUrl';
import {
  displayPromoterName,
  resolveEventDisplayName,
  resolveEventSubtitle,
} from '../utils/eventDisplayName';
import { formatEventDateLong, formatEventTime } from '../utils/eventDateFormat';
import { isFestivalEvent } from '../utils/eventType';
import { openFestivalPicks } from '../lib/festivalPicks';
import { stashPendingAction, readPendingAction, clearPendingAction } from '../lib/pendingAction';
import { usePromptAuth } from './auth/AuthPrompt';
import { markDone, useNudge } from '../lib/moments';
import {
  useRsvpWrite,
  runRsvpStatusWrite,
  runDeleteRsvpWrite,
} from '../hooks/mutations/useRsvpWrite';
import { useGroupMembershipWrite } from '../hooks/mutations/useGroupMembershipWrite';

import Modal, { ModalBody } from './ui/Modal';
import Button from './ui/Button';
import ShareIcon from './ui/ShareIcon';
import SearchField from './search/SearchField';
import StaleDataBanner from './ui/StaleDataBanner';
import { useEventDetail, useEventDetailRsvps } from '../hooks/queries/useEventDetail';
import { useFestivalMap } from '../hooks/queries/useFestivalMap';
import { queryKeys } from '../lib/query/queryKeys';
import { invalidateAfterRsvpChange, afterMatchRpc } from '../lib/query/invalidateMutations';
import { isEventCancelled } from '../utils/eventStatus';
import { markMapTap } from '../lib/map/openTiming';

/** How often to re-check queue placement while the user waits for a match. */
const QUEUE_POLL_MS = 60_000;

/** Single line for maps + clipboard: venue name, address, city, state (omit empties). */
function buildVenueLocationQuery(venue) {
  if (!venue) return '';
  return [venue.name, venue.address, venue.city, venue.state].filter(Boolean).join(', ');
}

// Ticketmaster's placeholders are not a genre: no label rather than "Undefined" (Sam, 2026-10-07: Darker Waves, For You Fest).
const NOT_A_GENRE = new Set(['', 'undefined', 'null', 'other', 'music']);
/** A person going, as a Groups-page row: face, name, and a tag for a friend (someone you follow) or for you. */
function GoingRow({ rsvp, name, friend, you, onOpen }) {
  return (
    <Link to={`/profile/${rsvp.user_id}`} onClick={onOpen} className="list-index__row !py-2.5">
      <Avatar displayName={name} profilePhotoUrl={rsvp.profiles?.profile_photo_url} size="sm" />
      <span className="list-index__text">
        <span className="list-index__name">{name}</span>
      </span>
      {friend ? (
        <span className="list-index__tag list-index__tag--on">Friend</span>
      ) : you ? (
        <span className="list-index__tag">You</span>
      ) : null}
    </Link>
  );
}

/** Ticketmaster's notes carry markdown links ("[Click here for Park and Ride](//www.ticketmaster.com/...)"): show the link text, tappable. */
function AboutText({ text }) {
  const parts = [];
  const re = /\[([^\]]+)\]\(((?:https?:)?\/\/[^\s)]+)\)/g;
  let last = 0;
  for (const m of text.matchAll(re)) {
    parts.push(text.slice(last, m.index));
    const href = m[2].startsWith('//') ? `https:${m[2]}` : m[2];
    parts.push(
      <a
        key={m.index}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline underline-offset-2"
      >
        {m[1]}
      </a>,
    );
    last = m.index + m[0].length;
  }
  parts.push(text.slice(last));
  return parts;
}

function genreLabel(genre) {
  const g = typeof genre === 'string' ? genre.trim() : '';
  return NOT_A_GENRE.has(g.toLowerCase()) ? null : g;
}

export default function EventDetail({ eventId, isModal, onClose, onUpdate }) {
  const promptAuth = usePromptAuth();
  const firstGoing = () => markDone('rsvp');
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { session } = useCurrentUserProfile();
  const queryClient = useQueryClient();
  const rsvpWrite = useRsvpWrite(session?.user?.id);
  const groupMembershipWrite = useGroupMembershipWrite(session?.user?.id);

  const {
    data: queriedEvent,
    isPending: eventQueryPending,
    isFetching: eventQueryFetching,
    isError: eventQueryIsError,
    error: eventQueryError,
    dataUpdatedAt: eventUpdatedAt,
  } = useEventDetail(eventId);
  const { data: queriedRsvps, isPending: rsvpsQueryPending } = useEventDetailRsvps(eventId);

  const [event, setEvent] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rsvpLoading, setRsvpLoading] = useState(false);

  // Groups state
  const [userGroups, setUserGroups] = useState([]);
  const [friendsGoing, setFriendsGoing] = useState([]);
  const [editRsvpOpen, setEditRsvpOpen] = useState(false);
  const [confirmUnRSVPModal, setConfirmUnRSVPModal] = useState({
    show: false,
    eventId: null,
    groups: [],
    hasQueueEntry: false,
  });
  const [isSolo, setIsSolo] = useState(false); // User is RSVPed but not in group and not in queue
  const [isInQueue, setIsInQueue] = useState(false); // User is in the matching queue
  const [queueCount, setQueueCount] = useState(0); // Total queue count for this event
  const [goingOptionsModal, setGoingOptionsModal] = useState({ show: false });
  const { canFindGroup } = useJoinableEventGroups(eventId, { enabled: Boolean(session) });
  const { data: headlinerMap = {} } = useEventHeadliners(eventId ? [eventId] : []);
  const [showFestivalSetupModal, setShowFestivalSetupModal] = useState(false);
  const [showGoingListModal, setShowGoingListModal] = useState(false);
  const [goingListSearchQuery, setGoingListSearchQuery] = useState('');
  const [venueActionsOpen, setVenueActionsOpen] = useState(false);
  // Map sits between Tickets and Share when the event has a published map (operator pick A, 2026-10-04).
  const { data: festivalMap } = useFestivalMap(eventId);
  const hasEventMap = Boolean(festivalMap);
  const [rejoinModal, setRejoinModal] = useState({
    show: false,
    group: null,
    eventId: null,
    skippedGroupIds: [],
    remainingCandidates: [],
  });

  useEffect(() => {
    setVenueActionsOpen(false);
  }, [eventId]);

  useEffect(() => {
    if (queriedEvent === null && !eventQueryPending && !eventQueryIsError) {
      setError('Event not found.');
      setEvent(null);
      setRsvps([]);
      setLoading(false);
      return;
    }
    if (eventQueryIsError && !queriedEvent && !event) {
      setError(eventQueryError?.message || 'Failed to load event.');
      setLoading(false);
      return;
    }
    if (eventQueryIsError && (queriedEvent || event)) {
      setError(null);
    }
    if (queriedEvent) {
      setEvent(queriedEvent);
      setError(null);
    }
    if (queriedRsvps) {
      setRsvps(queriedRsvps);
    }
    setLoading(eventQueryPending && !queriedEvent && !event);
  }, [
    queriedEvent,
    queriedRsvps,
    eventQueryPending,
    eventQueryIsError,
    eventQueryError,
    event,
    rsvpsQueryPending,
  ]);

  // Fetch groups for this event
  const fetchGroups = async () => {
    if (!eventId) return;

    // Fetch user's groups for this event
    if (session?.user?.id) {
      const { data: userGroupsData } = await supabase
        .from('group_members')
        .select(
          `
            group_id,
            groups!inner(
              id,
              name,
              event_id,
              status,
              formed_at
            )
          `,
        )
        .eq('user_id', session.user.id)
        .eq('groups.event_id', eventId)
        .is('left_at', null);

      if (userGroupsData) {
        // Get member counts (excluding left members)
        const groupIds = userGroupsData.map((g) => g.groups.id);
        const { data: memberCounts } = await supabase
          .from('group_members')
          .select('group_id, user_id, profiles:user_id(id, display_name, profile_photo_url)')
          .in('group_id', groupIds)
          .is('left_at', null);

        const countMap = {};
        const membersMap = {};
        memberCounts?.forEach((m) => {
          countMap[m.group_id] = (countMap[m.group_id] || 0) + 1;
          if (m.profiles) {
            membersMap[m.group_id] = [...(membersMap[m.group_id] || []), m.profiles];
          }
        });

        setUserGroups(
          userGroupsData.map((g) => ({
            ...g.groups,
            memberCount: countMap[g.groups.id] || 0,
            members: membersMap[g.groups.id] || [],
          })),
        );
      } else {
        setUserGroups([]);
      }

      // Check if user is solo: RSVPed but not in group and not in queue
      if (session?.user?.id) {
        // Check if user is RSVPed
        const { data: rsvpData } = await supabase
          .from('rsvps')
          .select('status')
          .eq('event_id', eventId)
          .eq('user_id', session.user.id)
          .eq('status', 'going')
          .maybeSingle();

        if (rsvpData) {
          // User is RSVPed - check if they're in a group or queue
          const hasGroup = userGroupsData && userGroupsData.length > 0;

          // Check if in queue
          const { data: queueData } = await supabase
            .from('group_matching_queue')
            .select('id')
            .eq('event_id', eventId)
            .eq('user_id', session.user.id)
            .eq('status', 'waiting')
            .maybeSingle();

          const hasQueueEntry = !!queueData;
          setIsInQueue(hasQueueEntry);

          // Fetch total queue count for this event
          if (hasQueueEntry) {
            const { count } = await supabase
              .from('group_matching_queue')
              .select('*', { count: 'exact', head: true })
              .eq('event_id', eventId)
              .eq('status', 'waiting');
            setQueueCount(count || 0);
          } else {
            setQueueCount(0);
          }

          // Solo = RSVPed but no group and no queue entry
          setIsSolo(!hasGroup && !hasQueueEntry);
        } else {
          setIsSolo(false);
        }
      } else {
        setIsSolo(false);
      }

      // Fetch friends going to this event
      const { data: followingData } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', session.user.id);

      if (followingData) {
        const followingIds = followingData.map((f) => f.following_id);
        if (followingIds.length > 0) {
          const { data: friendRsvps } = await supabase
            .from('rsvps')
            .select(
              `
                user_id,
                status,
                profiles:user_id(id, display_name, profile_photo_url)
              `,
            )
            .eq('event_id', eventId)
            .eq('status', 'going')
            .in('user_id', followingIds);

          if (friendRsvps) {
            setFriendsGoing(friendRsvps.map((r) => r.profiles).filter(Boolean));
          }
        }
      }
    }
  };

  useEffect(() => {
    fetchGroups();
  }, [eventId, session?.user?.id]);

  const fetchGroupsRef = useRef(fetchGroups);
  useEffect(() => {
    fetchGroupsRef.current = fetchGroups;
  });

  // Matching happens server-side with no webhook back to the client, and the nightly
  // sweep is far too slow to notice a group forming while the page is open — so poll
  // while (and only while) the user is sitting in the queue.
  useEffect(() => {
    if (!isInQueue || userGroups.length > 0) return undefined;
    const timer = setInterval(() => fetchGroupsRef.current?.(), QUEUE_POLL_MS);
    return () => clearInterval(timer);
  }, [isInQueue, userGroups.length]);

  const userRsvp = session && rsvps.find((r) => r.user_id === session.user?.id);
  const userStatus = userRsvp?.status || null;
  const joinGlow = useNudge('rsvp', userStatus !== 'going'); // the first-time path (lib/moments): Join glows until the first RSVP
  const goingCount = rsvps.filter((r) => r.status === 'going').length;

  const isActionClosed = isEventActionClosed(event);

  // Showtimes come from eventDateFormat so they render in the venue's zone
  // rather than the viewer's — a 7:00 PM Blue Note show read 10:00 PM on
  // Eastern time.

  // Signed out: the sign-in pop-up over this page (Stage 8, Sam 2026-10-23), named for the event. A stashed RSVP is
  // finished by the pending-action effect below as the session arrives; the pop-up's × drops it.
  const requireAuth = (action) => {
    if (!session) {
      if (action === 'rsvp_going' && eventId) {
        stashPendingAction({ type: 'rsvp_going', eventId });
      }
      promptAuth({ action: 'Join', target: resolveEventDisplayName(event) || 'this event' });
      return false;
    }
    return true;
  };

  const shareEventLink = async () => {
    if (!eventId) return;
    const shareUrl = buildAppUrl(`/events/${eventId}`);
    const displayName = resolveEventDisplayName(event);
    const result = await shareLink({
      url: shareUrl,
      title: displayName || 'Event on Prii',
      text: displayName ? `${displayName} on Prii` : 'An event on Prii',
    });
    if (result.method === 'clipboard') {
      showToast('Copied!');
    } else if (result.method === 'failed') {
      showToast('Failed to share link.');
    }
  };

  const refetchRsvps = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.events.detailRsvps(eventId) });
    invalidateAfterRsvpChange(queryClient, { userId: session?.user?.id, eventId });
  };

  const getDisplayName = (rsvp) => {
    const profiles = rsvp.profiles;
    if (profiles?.display_name?.trim()) return profiles.display_name.trim();
    if (profiles?.email) return profiles.email.split('@')[0];
    return 'Unknown';
  };

  // Helper function to get toast message based on event timing
  const getRsvpToastMessage = () => {
    return "You're looking for a group! We'll match you as soon as others join.";
  };

  const resetRejoinModal = () =>
    setRejoinModal({
      show: false,
      group: null,
      eventId: null,
      skippedGroupIds: [],
      remainingCandidates: [],
    });

  /** Apply `match_user_to_group` RPC result; returns true when client queue + handleGroupMatching should be skipped. */
  const applyPublicMatchRpcResult = async (rpcData, targetEventId) => {
    const classified = classifyMatchUserToGroupPayload(rpcData);
    switch (classified.kind) {
      case 'matched':
        showToast("You're matched into a group.");
        await fetchGroups();
        await refetchRsvps();
        onUpdate?.();
        navigate(`/groups/${classified.groupId}`);
        return true;
      case 'rejoin_available':
        if (classified.candidates.length === 0) return false;
        setRejoinModal({
          show: true,
          group: classified.candidates[0],
          eventId: targetEventId,
          skippedGroupIds: [],
          remainingCandidates: classified.candidates.slice(1),
        });
        return true;
      case 'queued':
        showToast(getRsvpToastMessage());
        setIsSolo(false);
        await fetchGroups();
        await refetchRsvps();
        onUpdate?.();
        return true;
      case 'closed':
        showToast('Matching is closed for this event.');
        return true;
      case 'rpc_error':
        showToast(classified.message || 'Could not save.');
        return true;
      default:
        return false;
    }
  };

  const handleRejoinGroup = async () => {
    const { group, eventId: rejoinEventId, skippedGroupIds } = rejoinModal;
    if (!group || !rejoinEventId) return;
    setRejoinModal({
      show: false,
      group: null,
      eventId: null,
      skippedGroupIds: [],
      remainingCandidates: [],
    });

    const { data, error } = await supabase.rpc('confirm_rejoin_group', {
      p_group_id: group.group_id,
    });
    if (error) {
      console.error('[EventDetail] confirm_rejoin_group error:', error);
      showToast('Failed to rejoin group.');
      return;
    }
    afterMatchRpc(queryClient, {
      userId: session?.user?.id,
      eventId: rejoinEventId,
      groupId: data?.group_id,
    }); // the RPC is the RSVP here: refresh Maps' list and start the map
    if (data.outcome === 'rejoined') {
      showToast("You've rejoined the group!");
      await fetchGroups();
      await refetchRsvps();
      onUpdate?.();
      navigate(`/groups/${data.group_id}`);
    } else if (data.outcome === 'full') {
      showToast('This group is now full. Finding another group...');
      const skipIds = [...(skippedGroupIds || []), group.group_id];
      const { data: matchData, error: matchErr } = await supabase.rpc('match_user_to_group', {
        p_event_id: rejoinEventId,
        p_skip_group_ids: skipIds,
      });
      if (matchErr) {
        showToast('Could not find a group.');
        return;
      }
      afterMatchRpc(queryClient, {
        userId: session?.user?.id,
        eventId: rejoinEventId,
        groupId: matchData?.group_id,
      }); // the RPC is the RSVP here: refresh Maps' list and start the map
      if (await applyPublicMatchRpcResult(matchData, rejoinEventId)) return;
    } else {
      showToast(data.message || 'Could not rejoin group.');
    }
  };

  const handleSkipRejoinGroup = async () => {
    const { group, eventId: rejoinEventId, skippedGroupIds, remainingCandidates } = rejoinModal;
    if (!group || !rejoinEventId) return;

    const newSkippedIds = [...skippedGroupIds, group.group_id];

    const nextCandidate = remainingCandidates.find((c) => !newSkippedIds.includes(c.group_id));

    if (nextCandidate) {
      setRejoinModal({
        show: true,
        group: nextCandidate,
        eventId: rejoinEventId,
        skippedGroupIds: newSkippedIds,
        remainingCandidates: remainingCandidates.filter(
          (c) => c.group_id !== nextCandidate.group_id,
        ),
      });
      return;
    }

    setRejoinModal({
      show: false,
      group: null,
      eventId: null,
      skippedGroupIds: [],
      remainingCandidates: [],
    });

    const { data, error } = await supabase.rpc('match_user_to_group', {
      p_event_id: rejoinEventId,
      p_skip_group_ids: newSkippedIds,
    });
    if (error) {
      showToast('Could not find a group.');
      return;
    }
    afterMatchRpc(queryClient, {
      userId: session?.user?.id,
      eventId: rejoinEventId,
      groupId: data?.group_id,
    }); // the RPC is the RSVP here: refresh Maps' list and start the map

    if (await applyPublicMatchRpcResult(data, rejoinEventId)) return;

    const cls = classifyMatchUserToGroupPayload(data);
    if (cls.kind === 'rejoin_available' && cls.candidates.length > 0) {
      setRejoinModal({
        show: true,
        group: cls.candidates[0],
        eventId: rejoinEventId,
        skippedGroupIds: newSkippedIds,
        remainingCandidates: cls.candidates.slice(1),
      });
    }
  };

  // Client-side group matching logic - forms groups earlier and grows them organically
  // Handle "Find a Group" button click - adds to queue and runs matching

  const setRsvpStatus = async (
    status,
    goingChoice = 'queue',
    previousStatus = null,
    groupName = null,
  ) => {
    if (status && isActionClosed) {
      throw new Error('Joining and group actions are closed for this event.');
    }

    return runRsvpStatusWrite({
      rsvpWrite,
      userId: session.user.id,
      eventId,
      status,
      goingChoice,
      previousStatus,
      groupName,
      getEventForCutoff: async () => event,
      showToast,
      navigate,
      setRejoinModal,
      getRsvpToastMessage: () => getRsvpToastMessage(null),
    });
  };

  const deleteRsvp = async (previousStatus = null) =>
    runDeleteRsvpWrite({
      rsvpWrite,
      userId: session.user.id,
      eventId,
      previousStatus,
    });

  const checkGroupsBeforeUnRSVP = async (eventId) => {
    // Check if user is in any groups for this event
    const { data: userGroupsData, error: groupsError } = await supabase
      .from('group_members')
      .select(
        `
        group_id,
        groups!inner(
          id,
          name,
          event_id
        )
      `,
      )
      .eq('user_id', session.user.id)
      .eq('groups.event_id', eventId)
      .is('left_at', null);

    if (groupsError) {
      console.error('[EventDetail] Error checking groups:', groupsError);
      // Continue to check queue even if groups check fails
    }

    // Check if user is in the matching queue (pending or matched)
    const { data: queueData, error: queueError } = await supabase
      .from('group_matching_queue')
      .select('id, status')
      .eq('user_id', session.user.id)
      .eq('event_id', eventId);

    if (queueError) {
      console.error('[EventDetail] Error checking queue:', queueError);
    }

    const hasGroups = userGroupsData && userGroupsData.length > 0;
    const hasQueueEntry = queueData && queueData.length > 0;

    // Every un-RSVP asks first (Sam's B, 2026-10-26): with no group it used to go straight through with only a toast.
    setConfirmUnRSVPModal({
      show: true,
      eventId: eventId,
      groups: hasGroups ? userGroupsData : [],
      hasQueueEntry: Boolean(hasQueueEntry),
    });
    return true; // The modal does the un-RSVP
  };

  const handleConfirmUnRSVP = async () => {
    const { groups } = confirmUnRSVPModal;

    try {
      // Get user profile for system messages
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', session.user.id)
        .single();

      const displayName = userProfile?.display_name || 'Someone';

      // Append voluntary leave rows, system messages, creator handoff, archive if empty
      if (groups.length > 0) {
        for (const g of groups) {
          await groupMembershipWrite.mutateAsync({
            action: 'leave',
            groupId: g.group_id,
            userId: session.user.id,
            displayName,
            groupName: g.groups?.name,
            postSystemMessage: true,
          });
        }
      }

      await deleteRsvp('going');

      // Close modal
      setConfirmUnRSVPModal({ show: false, eventId: null, groups: [], hasQueueEntry: false });

      // Show toast
      showToast("You're no longer marked as going.");

      // Refresh data
      await refetchRsvps();
      onUpdate?.();
    } catch (err) {
      console.error('[EventDetail] Error confirming un-RSVP:', err);
      showToast(err.message || 'Could not update your join status.');
    }
  };

  const handleRsvpGoing = async (confirmed = false) => {
    if (!requireAuth('rsvp_going')) return;
    // Going opens Edit RSVP first (Sam's B, 2026-11-04); its Not going comes back here confirmed.
    if (userStatus === 'going' && !confirmed) {
      setEditRsvpOpen(true);
      return;
    }
    setRsvpLoading(true);
    try {
      if (userStatus === 'going') {
        // User is removing their RSVP - check for groups first
        const hasGroups = await checkGroupsBeforeUnRSVP(eventId);
        if (hasGroups) {
          // Modal will handle the un-RSVP
          setRsvpLoading(false);
          return;
        }
        // No groups, proceed normally
        if (eventId) {
          localStorage.removeItem(`dismissed_group_prompt_${eventId}`);
        }
        await deleteRsvp();
        showToast("You're no longer marked as going.");
      } else {
        setGoingOptionsModal({ show: true });
        return;
      }
      await refetchRsvps();
      onUpdate?.();
    } catch (err) {
      console.error('[Join] handleRsvpGoing failed:', {
        eventId,
        source: 'event-detail',
        name: err?.name,
        message: err?.message,
        stack: err?.stack,
        fullError: err,
      });
      showToast(err.message || 'Could not save.');
    } finally {
      setRsvpLoading(false);
    }
  };

  const closeGoingOptionsModal = () => {
    setGoingOptionsModal({ show: false });
  };

  const isFestival = isFestivalEvent(event);

  const handleGoingChoice = async (choice, groupName = null) => {
    setGoingOptionsModal({ show: false });

    // Festivals: collect attendance days + lineup votes before entering the
    // match queue. The modal calls executeGoingChoice('queue') on completion.
    if (choice === 'queue' && isFestival && session?.user?.id) {
      setShowFestivalSetupModal(true);
      return;
    }

    await executeGoingChoice(choice, groupName);
  };

  const executeGoingChoice = async (choice, groupName = null) => {
    if (choice === 'skip' && userStatus === 'going') {
      return;
    }

    setRsvpLoading(true);
    try {
      appLog('debug', 'join.start', { eventId, goingChoice: choice, source: 'event-detail' });
      // Said on the tap, not after the round trip (2026-10-27 latency audit: on a weak signal the write retries for up
      // to ~2 min first). The page already shows you going; a refusal still gets its own toast from the catch below.
      if (choice === 'skip') showToast("You're going");
      const toastMessage = await setRsvpStatus('going', choice, null, groupName);
      appLog('debug', 'join.done', { eventId, goingChoice: choice, source: 'event-detail' });
      if (choice !== 'skip' && toastMessage) {
        showToast(toastMessage);
      }
      await refetchRsvps();
      firstGoing();
      onUpdate?.();
      // Just me on a festival: its picks come next (a new group opens the group; the queue took picks in its modal).
      if (isFestival && choice === 'skip') {
        void openFestivalPicks({
          queryClient,
          navigate,
          eventId,
          isFestival,
          backTo: `/events/${eventId}`,
        });
      }
    } catch (err) {
      console.error('[Join] failed:', {
        eventId,
        choice,
        source: 'event-detail',
        name: err?.name,
        message: err?.message,
        stack: err?.stack,
        fullError: err,
      });
      showToast(err.message || 'Could not save.');
    } finally {
      setRsvpLoading(false);
    }
  };

  const handleJoinExistingGroup = async (groupId) => {
    if (!eventId || !groupId) return;
    setGoingOptionsModal({ show: false });

    if (userStatus === 'going') {
      stashPendingAction({ type: 'join_group_chat', groupId });
      navigate(`/groups/${groupId}`);
      return;
    }

    setRsvpLoading(true);
    try {
      await setRsvpStatus('going', 'skip');
      await refetchRsvps();
      onUpdate?.();
      stashPendingAction({ type: 'join_group_chat', groupId });
      navigate(`/groups/${groupId}`);
    } catch (err) {
      console.error('[Join] join existing group failed:', {
        eventId,
        groupId,
        source: 'event-detail',
        name: err?.name,
        message: err?.message,
        stack: err?.stack,
        fullError: err,
      });
      showToast(err.message || 'Could not join group.');
    } finally {
      setRsvpLoading(false);
    }
  };

  useEffect(() => {
    if (!session?.user?.id || !eventId || loading || !event) return;

    const pending = readPendingAction();
    if (!pending || pending.type !== 'rsvp_going') return;
    if (String(pending.eventId) !== String(eventId)) return;

    if (userStatus === 'going') {
      clearPendingAction();
      return;
    }

    if (isActionClosed) {
      clearPendingAction();
      return;
    }

    clearPendingAction();

    void (async () => {
      try {
        setRsvpLoading(true);
        await setRsvpStatus('going', 'skip');
        showToast("You're in!");
        await refetchRsvps();
        firstGoing();
        onUpdate?.();
        // Same modal as Join while authed: queue / create / skip (already "going"; dismiss stays going / unmatched)
        setGoingOptionsModal({ show: true });
      } catch (err) {
        console.error('[EventDetail] pending join replay:', err);
        showToast(err?.message || 'Could not complete join.');
      } finally {
        setRsvpLoading(false);
      }
    })();
    // Replay once when session attaches on matching event — avoid re-running when handlers are recreated each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id, eventId, loading, event, userStatus, isActionClosed]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-500 dark:text-dark-muted">
        Loading event...
      </div>
    );
  }

  const showStaleBanner = Boolean(event) && eventQueryIsError;

  if ((error || !event) && !showStaleBanner) {
    return (
      <div className="p-6">
        <div className="rounded-lg bg-danger-fill text-danger p-4 mb-4">
          {error || 'Event not found.'}
        </div>
        {!isModal && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate(-1)}
            className="!h-auto px-1 !font-medium !text-primary hover:!underline"
          >
            Go back
          </Button>
        )}
      </div>
    );
  }

  // No network placeholder: with no art EventCardImage draws its own gradient, which works with no service too.
  const heroImage = getEventImageUrl(event, EVENT_IMAGE_SLOT.HERO) || null;
  const heroLayout = EVENT_IMAGE_LAYOUT.detailHero;
  const venueFullAddress =
    [event.venues?.address, event.venues?.city, event.venues?.state].filter(Boolean).join(', ') ||
    event.venues?.address ||
    event.address ||
    null;
  const venueLocationQuery = buildVenueLocationQuery(event.venues);
  const canOpenVenueActions = Boolean(venueLocationQuery.trim());
  const encodedVenueQuery = encodeURIComponent(venueLocationQuery);
  const appleMapsUrl = `https://maps.apple.com/?q=${encodedVenueQuery}`;
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedVenueQuery}`;
  const aboutText = (event.event_info || '').trim() || (event.please_note || '').trim();
  const ticketUrl = event.event_url || event.ticket_url;
  // One Going list (Sam, 2026-10-26: "Friends Going" was a second section further down): friends first, then you, then
  // everyone else. Everyone going is public; a friend wears a tag so they stand out in a long list.
  const friendIds = new Set(friendsGoing.map((f) => f.id));
  const goingRank = (r) => (friendIds.has(r.user_id) ? 0 : r.user_id === session?.user?.id ? 1 : 2);
  const goingList = rsvps
    .filter((r) => r.status === 'going')
    .sort((a, b) => goingRank(a) - goingRank(b));
  const INLINE_GOING_LIMIT = 5;
  const trimmedGoingSearch = goingListSearchQuery.trim().toLowerCase();
  const goingListModalFiltered = !trimmedGoingSearch
    ? goingList
    : goingList.filter((rsvp) => getDisplayName(rsvp).toLowerCase().includes(trimmedGoingSearch));

  // Extract Spotify artist ID from URL
  function extractSpotifyArtistId(url) {
    if (!url) return null;
    try {
      const pathname = new URL(url).pathname;
      const match = pathname.match(/^\/artist\/([a-zA-Z0-9]+)/);
      return match ? match[1] : null;
    } catch {
      return null;
    }
  }

  const eventDisplayName = resolveEventDisplayName(event);
  // The headliners we matched ourselves first, Ticketmaster's one Spotify link as the fallback (Sam, 2026-10-27: John
  // Summit and Malcolm Todd had none on Ticketmaster - 874 of 1,663 upcoming shows - and Lauryn Hill & Wyclef Jean's
  // listing linked only Lauryn Hill). One row per artist here, each with its own play - the page has the room the tiles
  // and Sets do not (they play co-headliners as tabs). Festivals have their Sets instead.
  const tmSpotifyId = event?.spotify_url ? extractSpotifyArtistId(event.spotify_url) : null;
  const ourHeadliners = (!isFestival && headlinerMap[eventId]) || [];
  const artistPlayers = ourHeadliners.length
    ? ourHeadliners
    : tmSpotifyId
      ? [{ spotifyId: tmSpotifyId, name: eventDisplayName || 'Artist', image: null }]
      : [];
  const hasSpotify = artistPlayers.length > 0;
  // Ticketmaster's Instagram/YouTube are the listing's main artist: on the row whose Spotify matches its link, else the first.
  const socialsRow = Math.max(
    0,
    artistPlayers.findIndex((a) => a.spotifyId === tmSpotifyId),
  );
  const hasInstagram = !!event?.instagram_url;
  const hasYoutube = !!event?.youtube_url;
  const eventSubtitle = resolveEventSubtitle(event);
  const promoterName = displayPromoterName(event?.promoter_name);

  return (
    <div className={`flex flex-col flex-1 min-h-0 text-base ${isModal ? 'h-full' : ''}`}>
      <StaleDataBanner
        visible={showStaleBanner}
        dataUpdatedAt={eventUpdatedAt}
        isFetching={eventQueryFetching}
        className="mx-4 mt-3 sm:mx-6"
      />
      {/* Scrollable content */}
      <div className="overflow-y-auto flex-1 min-h-0 pb-app-content md:pb-0">
        {/* Hero: 250px mobile, 300px desktop; gradient bottom; event name + promoter bottom-left; genre top-right */}
        <div className="relative w-full h-[250px] sm:h-[300px] overflow-hidden bg-gray-900">
          <EventCardImage
            src={heroImage}
            alt={eventDisplayName || 'Event'}
            width={heroLayout.width}
            height={heroLayout.height}
            loading="eager"
            className="absolute inset-0 w-full h-full object-cover object-center"
            placeholderClassName="absolute inset-0 w-full h-full"
          />
          <div
            className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"
            style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.4), transparent)' }}
          />
          {/* Back, over the image's top-left corner - the same round button the map and a festival's sets have (Sam,
            2026-10-07). Back through history when the app brought you here; home when the page was opened directly. */}
          {!isModal && (
            <button
              type="button"
              className="maps-page__back event-hero__back"
              aria-label="Back"
              onClick={() => ((window.history.state?.idx ?? 0) > 0 ? navigate(-1) : navigate('/'))}
            >
              <ChevronLeft size={18} aria-hidden="true" />
            </button>
          )}
          {genreLabel(event.genre) && (
            <span className="event-hero__genre">{genreLabel(event.genre)}</span>
          )}
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 z-10">
            <h1
              id="event-detail-title"
              className="text-xl sm:text-3xl font-bold text-ink drop-shadow-md"
            >
              {eventDisplayName || 'Untitled Event'}
            </h1>
            {eventSubtitle && (
              <p className="mt-1 text-sm text-ink/80 drop-shadow-sm line-clamp-2">
                {eventSubtitle}
              </p>
            )}
            {promoterName && (
              <p className="mt-1 text-sm text-ink/90 drop-shadow-sm">{promoterName}</p>
            )}
          </div>
        </div>

        <div className="bg-transparent -mt-1">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-6">
            {isEventCancelled(event) && (
              <div
                role="status"
                className="mb-6 rounded-xl border border-danger-ring bg-danger-fill px-4 py-3"
              >
                <p className="text-sm font-semibold text-danger">This event was cancelled.</p>
                <p className="mt-0.5 text-xs text-gray-600 dark:text-dark-muted">
                  It stays in your Past events and groups.
                </p>
              </div>
            )}
            {!isEventCancelled(event) && isActionClosed && (
              <div
                role="status"
                className="mb-6 rounded-xl border border-gray-200 dark:border-dark-border px-4 py-3"
              >
                <p className="text-sm font-semibold text-gray-900 dark:text-dark-text">
                  This event has ended.
                </p>
                <p className="mt-0.5 text-xs text-gray-600 dark:text-dark-muted">
                  It stays in your Past events and groups.
                </p>
              </div>
            )}
            {/* 1. Info blocks */}
            <div className="flex flex-col gap-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-primary/15 dark:bg-primary/20 flex items-center justify-center shrink-0 min-h-[44px] min-w-[44px]">
                  <Calendar className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-dark-muted uppercase tracking-wide">
                    Date
                  </p>
                  <p className="text-gray-900 dark:text-dark-text font-medium mt-0.5 text-base">
                    {formatEventDateLong(event) || 'TBA'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-primary/15 dark:bg-primary/20 flex items-center justify-center shrink-0 min-h-[44px] min-w-[44px]">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-dark-muted uppercase tracking-wide">
                    Time
                  </p>
                  <p className="text-gray-900 dark:text-dark-text font-medium mt-0.5 text-base">
                    {formatEventTime(event) || 'TBA'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-primary/15 dark:bg-primary/20 flex items-center justify-center shrink-0 min-h-[44px] min-w-[44px]">
                  <MapPin className="w-5 h-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 dark:text-dark-muted uppercase tracking-wide">
                    Venue
                  </p>
                  <div className="relative mt-0.5">
                    <Button
                      type="button"
                      variant="ghost"
                      fullWidth
                      aria-expanded={venueActionsOpen}
                      aria-haspopup="dialog"
                      aria-label={canOpenVenueActions ? 'Venue location options' : undefined}
                      disabled={!canOpenVenueActions}
                      onClick={() => canOpenVenueActions && setVenueActionsOpen((o) => !o)}
                      className={`!h-auto !px-0 min-w-0 w-full max-w-full !flex-col !items-start !justify-start !gap-1 !whitespace-normal !rounded-lg py-0.5 text-left transition-colors focus-visible:!ring-offset-white dark:focus-visible:!ring-offset-dark-card ${
                        canOpenVenueActions
                          ? 'cursor-pointer !font-normal underline decoration-primary/50 decoration-2 underline-offset-2 hover:!text-primary hover:!underline !text-gray-900 dark:!text-dark-text dark:!decoration-primary/40 dark:hover:!text-primary'
                          : '!cursor-not-allowed opacity-70'
                      }`}
                    >
                      <span className="block w-full min-w-0 break-words text-base font-medium text-gray-900 dark:text-dark-text">
                        {event.venues?.name || 'TBA'}
                      </span>
                      {venueFullAddress && (
                        <span className="block w-full min-w-0 break-words text-sm text-gray-500 dark:text-dark-muted">
                          {venueFullAddress}
                        </span>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {!isActionClosed && !isModal && (
              <EventRsvpBar
                going={Boolean(session) && userStatus === 'going'}
                loading={rsvpLoading}
                glowClass={joinGlow}
                onRsvp={() => handleRsvpGoing()}
              />
            )}
            {/* Full screen, Join / Going floats above the dock (EventRsvpBar) and Tickets / Map / Share stay here under
                the venue; in the modal Join stays here too (Sam, 2026-10-31) */}
            {!isActionClosed && (
              <div className={isModal ? 'my-8 py-2' : 'mb-10 md:mb-28'}>
                <div className="max-w-4xl mx-auto">
                  <div className="flex w-full max-w-[600px] mx-auto flex-col gap-3">
                    {isModal && (
                      <Button
                        type="button"
                        fullWidth
                        size="lg"
                        loading={rsvpLoading}
                        variant={session && userStatus === 'going' ? 'secondary' : 'primary'}
                        onClick={() => handleRsvpGoing()}
                        data-moment="rsvp"
                        className={`${joinGlow} min-h-[48px] gap-2 py-3 focus-visible:!ring-offset-white dark:focus-visible:!ring-offset-dark-card md:min-h-[52px]`}
                      >
                        {session && userStatus === 'going' ? (
                          <Check size={20} className="h-5 w-5 shrink-0" aria-hidden />
                        ) : (
                          <Plus className="h-5 w-5 shrink-0" strokeWidth={2.6} aria-hidden />
                        )}
                        {session ? (userStatus === 'going' ? 'Going' : 'Join') : 'Join'}
                      </Button>
                    )}
                    <div className={`flex items-stretch ${hasEventMap ? 'gap-2' : 'gap-3'}`}>
                      <Button
                        as="a"
                        href={ticketUrl || '#'}
                        target={ticketUrl ? '_blank' : undefined}
                        rel={ticketUrl ? 'noopener noreferrer' : undefined}
                        onClick={(e) => {
                          if (!ticketUrl) e.preventDefault();
                        }}
                        variant="secondary"
                        size="md"
                        leftIcon={<Ticket size={16} className="shrink-0" aria-hidden />}
                        className={`flex-1 min-w-0 justify-center whitespace-nowrap focus-visible:!ring-offset-white dark:focus-visible:!ring-offset-dark-card ${
                          hasEventMap ? '!gap-1 !px-1.5 min-[360px]:!gap-1.5 min-[360px]:!px-3' : ''
                        } ${!ticketUrl ? 'pointer-events-none cursor-not-allowed opacity-40' : ''}`}
                      >
                        Tickets
                      </Button>
                      {hasEventMap && (
                        <Button
                          type="button"
                          variant="secondary"
                          size="md"
                          onClick={() => {
                            markMapTap();
                            navigate(`/map/${encodeURIComponent(eventId)}`);
                          }}
                          leftIcon={<MapIcon size={16} className="shrink-0" aria-hidden />}
                          className="flex-1 min-w-0 justify-center whitespace-nowrap !gap-1 !px-1.5 min-[360px]:!gap-1.5 min-[360px]:!px-3 focus-visible:!ring-offset-white dark:focus-visible:!ring-offset-dark-card"
                        >
                          Map
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="secondary"
                        size="md"
                        onClick={shareEventLink}
                        leftIcon={<ShareIcon size={16} className="shrink-0" aria-hidden />}
                        className={`flex-1 min-w-0 justify-center whitespace-nowrap focus-visible:!ring-offset-white dark:focus-visible:!ring-offset-dark-card ${hasEventMap ? '!gap-1 !px-1.5 min-[360px]:!gap-1.5 min-[360px]:!px-3' : ''}`}
                      >
                        Share
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Directly below the Join button: a festival's lineup, then your group - on every event, one card
                (Sam, 2026-10-26: regular events had an older "Your Groups" list further down). */}
            {/* Sets stay once the event is over, to look back on (Sam, 2026-10-27: ratings will hang off them); the group
                card is for joining, so it goes. A cancelled festival shows neither. */}
            {((isFestival && !isEventCancelled(event)) || (!isActionClosed && session)) && (
              <div className="mb-10 space-y-6">
                {isFestival && !isEventCancelled(event) && (
                  <EventLineupSection
                    eventId={eventId}
                    userId={session?.user?.id}
                    rsvpStatus={userStatus}
                  />
                )}
                {!isActionClosed && session && (
                  <FestivalGroupCard
                    groups={userGroups}
                    isInQueue={isInQueue}
                    isSolo={isSolo}
                    queueCount={queueCount}
                    userStatus={userStatus}
                    eventDatetime={event?.event_datetime}
                    canFindGroup={canFindGroup}
                    onCreateGroup={() => setGoingOptionsModal({ show: true, step: 'name' })}
                    onFindGroup={() => setGoingOptionsModal({ show: true, step: 'search' })}
                    onNavigate={onClose}
                  />
                )}
              </div>
            )}

            {/* Going: one list, friends first, five rows then See all (Sam, 2026-10-26) */}
            <section className="mb-10">
              <h2 className="lineup-section-label">
                Going{goingCount > 0 ? ` · ${goingCount}` : ''}
              </h2>
              {goingCount === 0 ? (
                <p className="text-meta text-ink/60">No one going yet.</p>
              ) : (
                <>
                  <ul className="list-index__list">
                    {goingList.slice(0, INLINE_GOING_LIMIT).map((rsvp) => (
                      <li key={rsvp.id}>
                        <GoingRow
                          rsvp={rsvp}
                          name={getDisplayName(rsvp)}
                          friend={friendIds.has(rsvp.user_id)}
                          you={rsvp.user_id === session?.user?.id}
                          onOpen={() => onClose?.()}
                        />
                      </li>
                    ))}
                  </ul>
                  {goingList.length > INLINE_GOING_LIMIT && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="md"
                      fullWidth
                      onClick={() => {
                        setGoingListSearchQuery('');
                        setShowGoingListModal(true);
                      }}
                      className="mt-3"
                    >
                      See all {goingCount}
                    </Button>
                  )}
                </>
              )}
            </section>

            {!isActionClosed && (hasSpotify || hasInstagram || hasYoutube) && (
              <section className="mb-10">
                {/* Social links: the artist as one row (Sam's B, 2026-10-26) */}
                <h2 className="lineup-section-label">
                  {artistPlayers.length > 1 ? 'Artists' : 'Artist'}
                </h2>
                {(artistPlayers.length ? artistPlayers : [null]).map((artist, i) => (
                  <ArtistLinksRow
                    key={artist?.spotifyId ?? 'event'}
                    eventId={eventId}
                    playKey={artist ? `event:${eventId}:${artist.spotifyId}` : null}
                    name={
                      artistPlayers.length > 1
                        ? artist.name
                        : eventDisplayName || artist?.name || 'Artist'
                    }
                    imageUrl={artistPlayers.length > 1 ? artist.image || heroImage : heroImage}
                    players={artist ? [artist] : []}
                    instagramUrl={i === socialsRow && hasInstagram ? event.instagram_url : null}
                    youtubeUrl={i === socialsRow && hasYoutube ? event.youtube_url : null}
                  />
                ))}
              </section>
            )}

            {/* 3. About at bottom */}
            {aboutText && (
              <section className="mb-8">
                <h2 className="lineup-section-label">About</h2>
                <p
                  className="text-ink/60 leading-relaxed whitespace-pre-wrap max-w-[600px]"
                  style={{ fontSize: '1rem' }}
                >
                  <AboutText text={aboutText} />
                </p>
              </section>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={showGoingListModal}
        onClose={() => {
          setShowGoingListModal(false);
          setGoingListSearchQuery('');
        }}
        title={`Going (${goingCount})`}
        size="xl"
      >
        <ModalBody className="flex flex-col min-h-0 pt-2">
          <div className="sticky top-0 isolate bg-black/30 backdrop-blur-md pb-3 -mx-6 px-6 border-b border-white/10 shrink-0">
            <SearchField
              value={goingListSearchQuery}
              onChange={(e) => setGoingListSearchQuery(e.target.value)}
              placeholder="Search by name..."
              aria-label="Search going list"
            />
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto -mx-2 px-2 py-4 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:dark:bg-dark-bg [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:dark:bg-dark-border [&::-webkit-scrollbar-thumb]:rounded-full">
            {goingListModalFiltered.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-dark-muted">
                {trimmedGoingSearch
                  ? `No one matches "${goingListSearchQuery.trim()}"`
                  : 'No one going yet.'}
              </div>
            ) : (
              <ul className="list-index__list">
                {goingListModalFiltered.map((rsvp) => {
                  return (
                    <li key={rsvp.id}>
                      <GoingRow
                        rsvp={rsvp}
                        name={getDisplayName(rsvp)}
                        friend={friendIds.has(rsvp.user_id)}
                        you={rsvp.user_id === session?.user?.id}
                        onOpen={() => {
                          onClose?.();
                          setShowGoingListModal(false);
                          setGoingListSearchQuery('');
                        }}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </ModalBody>
      </Modal>

      <RejoinModal
        isOpen={rejoinModal.show}
        onClose={resetRejoinModal}
        onRejoin={handleRejoinGroup}
        onFindDifferent={handleSkipRejoinGroup}
        groupName={rejoinModal.group?.group_name}
      />

      <EditRsvpSheet
        isOpen={editRsvpOpen}
        onClose={() => setEditRsvpOpen(false)}
        onNotGoing={() => {
          setEditRsvpOpen(false);
          handleRsvpGoing(true);
        }}
      />

      {/* Un-RSVP Confirmation Modal */}
      <UnRsvpConfirm
        isOpen={confirmUnRSVPModal.show}
        onClose={() =>
          setConfirmUnRSVPModal({ show: false, eventId: null, groups: [], hasQueueEntry: false })
        }
        onConfirm={handleConfirmUnRSVP}
        groups={confirmUnRSVPModal.groups}
        hasQueueEntry={confirmUnRSVPModal.hasQueueEntry}
      />

      <Modal
        isOpen={!!(venueActionsOpen && canOpenVenueActions)}
        onClose={() => setVenueActionsOpen(false)}
        title="Open in Maps"
        size="sm"
      >
        <ModalBody className="space-y-3 pt-2">
          <Button
            type="button"
            fullWidth
            onClick={() => {
              window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
              setVenueActionsOpen(false);
            }}
          >
            Open in Google Maps
          </Button>
          <Button
            type="button"
            variant="secondary"
            fullWidth
            onClick={() => {
              window.open(appleMapsUrl, '_blank', 'noopener,noreferrer');
              setVenueActionsOpen(false);
            }}
          >
            Open in Apple Maps
          </Button>
          <Button
            type="button"
            variant="ghost"
            fullWidth
            onClick={async () => {
              try {
                await copyTextToClipboard(venueLocationQuery);
                showToast('Copied!');
                setVenueActionsOpen(false);
              } catch (err) {
                console.error(
                  '[EventDetail] copy venue address failed:',
                  err?.name || 'Error',
                  err?.message || err,
                );
                showToast('Could not copy');
                setVenueActionsOpen(false);
              }
            }}
          >
            Copy Address
          </Button>
        </ModalBody>
      </Modal>

      <RsvpGoingOptionsModal
        isOpen={goingOptionsModal.show}
        initialStep={goingOptionsModal.step ?? 'choices'}
        onClose={closeGoingOptionsModal}
        onMatchMe={() => handleGoingChoice('queue')}
        onCreateGroup={(name) => handleGoingChoice('create', name)}
        onSkip={() => handleGoingChoice('skip')}
        onJoinExistingGroup={handleJoinExistingGroup}
        eventId={eventId}
        eventName={eventDisplayName}
        sharedInterestPanel={
          !isFestival && session?.user?.id ? (
            <MutualVotersPanel eventId={eventId} userId={session.user.id} className="mt-3" />
          ) : null
        }
      />

      <FestivalMatchSetupModal
        isOpen={showFestivalSetupModal}
        onClose={() => setShowFestivalSetupModal(false)}
        eventId={eventId}
        eventName={eventDisplayName}
        userId={session?.user?.id}
        onComplete={async () => {
          setShowFestivalSetupModal(false);
          await executeGoingChoice('queue');
        }}
      />
    </div>
  );
}
